/**
 * AuthDebugPanel — Debug auth state, JWT claims, and view layer resolution.
 *
 * Shows:
 *   - JWT claims (decoded from token)
 *   - Auth context state
 *   - Backend myViewLayer response
 *   - Scope list
 *
 * Features:
 *   - Scrollable content
 *   - Copy JSON button for sharing
 *   - Collapsible sections
 *
 * Toggle with Ctrl+Shift+A or via the pill in bottom-left.
 */

import { useState, useEffect, useMemo } from 'react';
import {
  Paper,
  Stack,
  Group,
  Text,
  Badge,
  ActionIcon,
  Collapse,
  Tooltip,
  ScrollArea,
  Code,
  CopyButton,
  Divider,
  ThemeIcon,
  Kbd,
  Alert,
  Button,
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import {
  IconShield,
  IconChevronDown,
  IconChevronRight,
  IconCopy,
  IconCheck,
  IconX,
  IconKey,
  IconUser,
  IconEye,
  IconList,
} from '@tabler/icons-react';

import { useAuth, useViewLayer, useTenantId } from '@/auth/AuthContext';
import { useMyViewLayerQuery } from '@/generated/graphql';
import { useNavigation } from '@/contexts/NavigationContext';

interface JWTClaims {
  sub?: string;
  email?: string;
  name?: string;
  groups?: string[];
  ferp_roles?: string[];
  ferp_scopes?: string[];
  ferp_tenant?: string;
  ferp_tenant_num?: number;
  exp?: number;
  iat?: number;
  [key: string]: unknown;
}

function decodeJWT(token: string): JWTClaims | null {
  try {
    const parts = token.split('.');
    if (parts.length !== 3) return null;
    const payloadPart = parts[1];
    if (!payloadPart) return null;
    const payload = JSON.parse(atob(payloadPart));
    return payload;
  } catch {
    return null;
  }
}

export function AuthDebugPanel() {
  const [expanded, setExpanded] = useState(false);
  const [showJwt, { toggle: toggleJwt }] = useDisclosure(true);
  const [showAuth, { toggle: toggleAuth }] = useDisclosure(true);
  const [showBackend, { toggle: toggleBackend }] = useDisclosure(true);
  const [showScopes, { toggle: toggleScopes }] = useDisclosure(false);

  const auth = useAuth();
  const viewLayer = useViewLayer();
  const tenantId = useTenantId();

  // Get backend view layer
  const { data: backendData, loading: backendLoading, error: backendError } = useMyViewLayerQuery({
    skip: !auth.isAuthenticated,
    fetchPolicy: 'network-only',
  });

  // DRR-313/315: Use shared NavigationContext for resolved scopes
  const { userScopes, loading: scopeLoading } = useNavigation();
  const resolvedScopes = Array.from(userScopes);

  // Decode JWT
  const jwtClaims = useMemo(() => {
    // Token is stored as 'auth:id_token' by AuthContext (not 'token')
    const token = sessionStorage.getItem('auth:id_token');
    if (!token) return null;
    return decodeJWT(token);
  }, [auth.isAuthenticated]);

  // Build debug object for copying
  const debugData = useMemo(() => ({
    timestamp: new Date().toISOString(),
    frontend: {
      viewLayer,
      isAuthenticated: auth.isAuthenticated,
      user: auth.user ? {
        email: auth.user.email,
        name: auth.user.displayName,
        roles: auth.user.roles,
      } : null,
      tenant: auth.tenant,
      devOverride: sessionStorage.getItem('portal:viewLayer'),
    },
    backend: {
      myViewLayer: backendData?.myViewLayer ?? null,
      loading: backendLoading,
      error: backendError?.message ?? null,
      resolvedScopeCount: resolvedScopes.length,
      scopeLoading,
    },
    jwt: jwtClaims ? {
      email: jwtClaims.email,
      groups: jwtClaims.groups,
      ferp_roles: jwtClaims.ferp_roles,
      ferp_tenant: jwtClaims.ferp_tenant,
      ferp_tenant_num: jwtClaims.ferp_tenant_num,
      exp: jwtClaims.exp ? new Date(jwtClaims.exp * 1000).toISOString() : null,
    } : null,
  }), [auth, viewLayer, backendData, backendLoading, backendError, jwtClaims, resolvedScopes, scopeLoading]);

  // Keyboard shortcut: Ctrl+Shift+A
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.ctrlKey && e.shiftKey && e.key === 'A') {
        e.preventDefault();
        setExpanded(prev => !prev);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  // Minimized pill
  if (!expanded) {
    return (
      <Tooltip label={<>Auth Debug <Kbd size="xs">Ctrl+Shift+A</Kbd></>}>
        <Paper
          shadow="md"
          radius="xl"
          style={{
            position: 'fixed',
            bottom: 16,
            right: 16,
            zIndex: 1100,
            cursor: 'pointer',
            padding: '6px 14px',
          }}
          onClick={() => setExpanded(true)}
        >
          <Group gap="xs">
            <ThemeIcon size="xs" radius="xl" color="cyan" variant="filled">
              <IconShield size={10} />
            </ThemeIcon>
            <Text size="xs" fw={600}>Auth</Text>
            <Badge size="xs" color={viewLayer === 'architect' ? 'violet' : viewLayer === 'admin' ? 'blue' : 'green'}>
              {viewLayer}
            </Badge>
          </Group>
        </Paper>
      </Tooltip>
    );
  }

  // Expanded panel
  return (
    <Paper
      shadow="xl"
      radius="md"
      p="md"
      withBorder
      style={{
        position: 'fixed',
        bottom: 16,
        left: 16,
        width: 480,
        maxHeight: 'calc(100vh - 100px)',
        zIndex: 1100,
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Stack gap="sm" style={{ flex: 1, minHeight: 0, overflow: 'hidden' }}>
        {/* Header */}
        <Group justify="space-between">
          <Group gap="xs">
            <ThemeIcon size="sm" color="cyan" variant="light">
              <IconShield size={14} />
            </ThemeIcon>
            <Text size="sm" fw={600}>Auth Debug</Text>
            <Kbd size="xs">Ctrl+Shift+A</Kbd>
          </Group>
          <Group gap="xs">
            <CopyButton value={JSON.stringify(debugData, null, 2)}>
              {({ copied, copy }) => (
                <Button
                  size="xs"
                  variant={copied ? 'filled' : 'light'}
                  color={copied ? 'teal' : 'gray'}
                  leftSection={copied ? <IconCheck size={14} /> : <IconCopy size={14} />}
                  onClick={copy}
                >
                  {copied ? 'Copied!' : 'Copy JSON'}
                </Button>
              )}
            </CopyButton>
            <ActionIcon variant="subtle" size="sm" onClick={() => setExpanded(false)}>
              <IconX size={14} />
            </ActionIcon>
          </Group>
        </Group>

        {/* Summary badges */}
        <Group gap="xs">
          <Badge size="sm" color={viewLayer === 'architect' ? 'violet' : viewLayer === 'admin' ? 'blue' : 'green'}>
            Layer: {viewLayer}
          </Badge>
          <Badge size="sm" color={backendData?.myViewLayer ? 'teal' : 'gray'} variant="light">
            Backend: {backendData?.myViewLayer ?? (backendLoading ? 'loading...' : 'none')}
          </Badge>
          {auth.user?.roles?.length && (
            <Badge size="sm" variant="outline">
              {auth.user.roles.length} role(s)
            </Badge>
          )}
        </Group>

        <Divider />

        {/* Scrollable content */}
        <ScrollArea style={{ flex: 1 }} offsetScrollbars>
          <Stack gap="sm">
            {/* JWT Section */}
            <Group gap="xs" style={{ cursor: 'pointer' }} onClick={toggleJwt}>
              <ActionIcon variant="subtle" size="xs">
                {showJwt ? <IconChevronDown size={14} /> : <IconChevronRight size={14} />}
              </ActionIcon>
              <IconKey size={16} />
              <Text size="sm" fw={500}>JWT Claims</Text>
            </Group>
            <Collapse in={showJwt}>
              {jwtClaims ? (
                <Code block style={{ fontSize: 11, maxHeight: 200, overflow: 'auto' }}>
                  {JSON.stringify({
                    email: jwtClaims.email,
                    groups: jwtClaims.groups,
                    ferp_roles: jwtClaims.ferp_roles,
                    ferp_tenant: jwtClaims.ferp_tenant,
                    ferp_tenant_num: jwtClaims.ferp_tenant_num,
                  }, null, 2)}
                </Code>
              ) : (
                <Alert color="yellow" variant="light" p="xs">
                  <Text size="xs">No token found in sessionStorage</Text>
                </Alert>
              )}
            </Collapse>

            {/* Auth Context Section */}
            <Group gap="xs" style={{ cursor: 'pointer' }} onClick={toggleAuth}>
              <ActionIcon variant="subtle" size="xs">
                {showAuth ? <IconChevronDown size={14} /> : <IconChevronRight size={14} />}
              </ActionIcon>
              <IconUser size={16} />
              <Text size="sm" fw={500}>Auth Context (Frontend)</Text>
            </Group>
            <Collapse in={showAuth}>
              <Code block style={{ fontSize: 11, maxHeight: 200, overflow: 'auto' }}>
                {JSON.stringify({
                  viewLayer,
                  isAuthenticated: auth.isAuthenticated,
                  user: auth.user ? {
                    email: auth.user.email,
                    name: auth.user.displayName,
                    roles: auth.user.roles,
                  } : null,
                  tenant: auth.tenant,
                  devOverride: sessionStorage.getItem('portal:viewLayer'),
                }, null, 2)}
              </Code>
            </Collapse>

            {/* Backend Section */}
            <Group gap="xs" style={{ cursor: 'pointer' }} onClick={toggleBackend}>
              <ActionIcon variant="subtle" size="xs">
                {showBackend ? <IconChevronDown size={14} /> : <IconChevronRight size={14} />}
              </ActionIcon>
              <IconEye size={16} />
              <Text size="sm" fw={500}>Backend Response</Text>
            </Group>
            <Collapse in={showBackend}>
              {backendError ? (
                <Alert color="red" variant="light" p="xs">
                  <Text size="xs">{backendError.message}</Text>
                </Alert>
              ) : (
                <Code block style={{ fontSize: 11 }}>
                  {JSON.stringify({
                    myViewLayer: backendData?.myViewLayer ?? null,
                    loading: backendLoading,
                  }, null, 2)}
                </Code>
              )}
            </Collapse>

            {/* Scopes Section (resolved from backend, not JWT) */}
            <Group gap="xs" style={{ cursor: 'pointer' }} onClick={toggleScopes}>
              <ActionIcon variant="subtle" size="xs">
                {showScopes ? <IconChevronDown size={14} /> : <IconChevronRight size={14} />}
              </ActionIcon>
              <IconList size={16} />
              <Text size="sm" fw={500}>
                Resolved Scopes ({scopeLoading ? '...' : resolvedScopes.length})
              </Text>
              {resolvedScopes.length > 0 && (
                <Badge size="xs" color="green" variant="light">from backend</Badge>
              )}
            </Group>
            <Collapse in={showScopes}>
              {scopeLoading ? (
                <Text size="xs" c="dimmed">Loading scopes from backend...</Text>
              ) : resolvedScopes.length > 0 ? (
                <Stack gap={4}>
                  <Text size="xs" c="dimmed">
                    DRR-315: Scopes resolved via ferp_roles → scope rules (sr-*.yaml)
                  </Text>
                  <ScrollArea h={150} offsetScrollbars>
                    <Group gap={4}>
                      {resolvedScopes.map((scope, i) => (
                        <Badge key={i} size="xs" variant="outline">
                          {scope}
                        </Badge>
                      ))}
                    </Group>
                  </ScrollArea>
                </Stack>
              ) : (
                <Text size="xs" c="dimmed">No scopes resolved (check JWT ferp_roles)</Text>
              )}
            </Collapse>
          </Stack>
        </ScrollArea>

        {/* Footer */}
        <Text size="xs" c="dimmed">
          View layer mismatch? Check JWT ferp_roles → scope rule → SaaSRole → myViewLayer chain.
        </Text>
      </Stack>
    </Paper>
  );
}
